import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Honest live waitlist counter.
 * - Below threshold: shows mystical "first circle" framing.
 * - At/above threshold: rounds down to nearest 10 and shows "Join {n}+ artists already on the WYNN list".
 *
 * Props:
 *   sourcePrefix: string e.g. "wynn_app" matches wynn_app, wynn_app_beta, wynn_app_final
 *   threshold: number, count needed before showing the number (default 50)
 *   firstCircleCopy: copy shown below threshold
 *   testId: optional data-testid
 *   className: optional extra classes
 */
export default function WaitlistCount({
  sourcePrefix,
  threshold = 50,
  firstCircleCopy = "Be among the first to step inside the first quiet circle.",
  testId = "waitlist-count",
  className = "",
}) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/subscribers/count`, {
        params: sourcePrefix ? { source_prefix: sourcePrefix } : {},
      })
      .then((res) => {
        if (active) setCount(res.data?.count ?? 0);
      })
      .catch(() => {
        if (active) setCount(0);
      });
    return () => {
      active = false;
    };
  }, [sourcePrefix]);

  // While loading, render the mystical copy (avoids layout shift + never lies)
  if (count === null || count < threshold) {
    return (
      <p
        data-testid={testId}
        className={`font-body text-xs text-[#F4F1EC]/55 leading-relaxed tracking-wide ${className}`}
      >
        {firstCircleCopy}
      </p>
    );
  }

  const rounded = Math.floor(count / 10) * 10;
  return (
    <p
      data-testid={testId}
      className={`font-body text-xs text-[#F4F1EC]/70 leading-relaxed tracking-wide ${className}`}
    >
      <span className="text-[#FFB7C6]">●</span>{" "}
      Join <span className="text-[#F4F1EC] font-medium">{rounded}+</span>{" "}
      artists already on the WYNN list.
    </p>
  );
}
